import { z } from 'zod';
import { FORMA_PAGAMENTO, SITUACAO_PAGAMENTO_MARCACAO, TIPO_MARCACAO, } from '../dominio/index.js';
import { dataValida } from '../tempo/index.js';
import { zHoraMinuto } from './configuracoes.js';
import { zCentavos, zDataISO, zId, zTelefone } from './primitivos.js';
/**
 * Os contratos da agenda — `#v-agenda`, `#m-reserva` e `#m-evento`.
 *
 * A entrada fala a língua da recepção: DATA e HORA LOCAIS ("terça,
 * 19:00, uma hora"), nunca instante. Quem converte para `timestamptz`
 * é o servidor, com o fuso da arena — a conversão acontece na borda e
 * num lugar só. O navegador nunca calcula um instante, e por isso o
 * fuso do computador da recepção é irrelevante.
 *
 * A saída devolve instantes ISO mais o `fuso` da arena; o frontend lê
 * o relógio de parede com `@arena/shared/tempo`.
 *
 * Ver: Arena_backend/Docs/plano/06-agenda-quadras.md
 */
// ---------------------------------------------------------------------
// Vocabulário
// ---------------------------------------------------------------------
/**
 * Os três tipos que operam neste marco.
 *
 * Os cinco existem como modelo desde já — a tabela é uma só —, mas
 * turma fixa nasce da tela de Turmas (documento 10) e aula experimental
 * nasce do pedido do site (documento 14). Os dois reusam o mesmo motor
 * de conflito e série quando chegarem.
 */
export const TIPOS_OPERANTES = ['LocacaoAvulsa', 'Evento', 'Bloqueio'];
export const ROTULO_TIPO_MARCACAO = {
    TurmaFixa: 'Turma fixa',
    LocacaoAvulsa: 'Locação avulsa',
    Experimental: 'Aula experimental',
    Evento: 'Evento',
    Bloqueio: 'Bloqueio',
};
/**
 * As situações que a recepção ainda tem que resolver — o que entra em
 * "horas a cobrar" do painel e do financeiro.
 *
 * `null` (pagamento a definir) e `APagar` são óbvios. `Sinal` entra
 * porque sinal é entrada PARCIAL: o resto do valor continua a receber.
 * `Isento` e `NaMensalidade` saem — não há o que receber na porta.
 */
export const SITUACOES_EM_ABERTO = [
    'APagar',
    'Sinal',
];
export function pagamentoEmAberto(situacao) {
    return situacao === null || situacao === undefined
        ? true
        : SITUACOES_EM_ABERTO.includes(situacao);
}
/**
 * A grade anda de meia em meia hora.
 *
 * O protótipo só oferece hora cheia e duração de 1h, 1h30 e 2h — o que
 * já exige a meia hora. Granularidade menor fica registrada como
 * pergunta ao dono no documento 06.
 */
export const PASSO_DA_GRADE_MINUTOS = 30;
export const DURACAO_MAXIMA_MINUTOS = 8 * 60;
/**
 * "Como no Google Agenda: a cor padrão vem da quadra e pode ser
 * sobrescrita" — `#m-reserva`. `null` é a cor da quadra. Os nomes são
 * tokens do protótipo, não hexadecimais: a paleta mora no CSS.
 */
export const CORES_DE_CARD = ['vermelho', 'laranja', 'azul', 'vinho'];
/**
 * A pergunta que o protótipo exige ao mexer numa ocorrência de série:
 * "somente esta aula ou toda a série?".
 *
 * `serie` vale DESTA OCORRÊNCIA EM DIANTE, nunca para trás. O passado
 * da série é histórico — é o dado que as métricas do documento 16 vão
 * ler — e reescrevê-lo porque a aula mudou de horário em outubro
 * falsificaria setembro.
 */
export const ESCOPOS_DE_ALTERACAO = ['ocorrencia', 'serie'];
/**
 * Quantas semanas de uma série existem como linha na tabela.
 *
 * As ocorrências são MATERIALIZADAS — é o que permite mover uma aula
 * isolada e detectar conflito com precisão. O resto do futuro é
 * materializado aos poucos pelo job do documento 07.
 */
export const HORIZONTE_DA_SERIE_SEMANAS = 12;
// ---------------------------------------------------------------------
// Peças
// ---------------------------------------------------------------------
/** Data de calendário que existe: o formato sozinho aceita 30 de fevereiro. */
export const zData = zDataISO.refine(dataValida, 'Essa data não existe no calendário.');
/** "19:00" ou "19:30" — a grade não tem 19:10. */
export const zHoraDaGrade = zHoraMinuto.refine((hora) => Number(hora.slice(3)) % PASSO_DA_GRADE_MINUTOS === 0, 'Use hora cheia ou meia hora — 19:00, 19:30.');
export const zDuracaoMinutos = z
    .number({ invalid_type_error: 'Informe a duração em minutos.' })
    .int()
    .min(PASSO_DA_GRADE_MINUTOS, 'A marcação dura ao menos meia hora.')
    .max(DURACAO_MAXIMA_MINUTOS, 'A marcação dura no máximo 8 horas.')
    .refine((m) => m % PASSO_DA_GRADE_MINUTOS === 0, 'A duração anda de meia em meia hora.');
/** Dias da semana: 0 = domingo … 6 = sábado. Repetidos são descartados. */
export const zDiasDaSemana = z
    .array(z.number().int().min(0).max(6))
    .min(1, 'Escolha ao menos um dia da semana.')
    .transform((dias) => [...new Set(dias)].sort((a, b) => a - b));
/**
 * "Repetir toda semana". Sem `ate`, a série não tem fim — a turma de
 * terça e quinta continua até alguém encerrá-la.
 */
export const zRecorrencia = z.object({
    diasSemana: zDiasDaSemana,
    ate: zData.optional(),
});
const texto = (maximo) => z.string().trim().max(maximo, `No máximo ${maximo} caracteres.`);
/** Quando e onde — a parte que o motor de conflito lê. */
const quando = {
    quadraId: zId,
    data: zData,
    inicio: zHoraDaGrade,
    duracaoMinutos: zDuracaoMinutos,
};
const apresentacao = {
    observacao: texto(500).optional(),
    cor: z.enum(CORES_DE_CARD).nullable().optional(),
    recorrencia: zRecorrencia.optional(),
};
/**
 * Valor e pagamento. O protótipo mostra "Pix — pago", "Pix — sinal",
 * "Dinheiro na chegada", "Cartão" e "Lançar na mensalidade": são pares
 * (forma, situação), e é assim que ficam gravados — "sinal" é
 * situação, não forma de pagamento.
 */
const cobranca = {
    valor: zCentavos.optional(),
    formaPagamento: z.enum(FORMA_PAGAMENTO).optional(),
    situacaoPagamento: z.enum(SITUACAO_PAGAMENTO_MARCACAO).optional(),
};
/**
 * A DATA do recebimento não entra aqui, na criação, e isso é escolha.
 *
 * Reserva nasce para acontecer; pagamento com data retroativa é correção
 * de algo já registrado, e correção tem caminho próprio — a baixa
 * (`zBaixaDePagamento`). Marcar "pago" ao criar continua valendo: o
 * servidor grava a data de HOJE, que é quando a recepção recebeu.
 */
/** "Pago em" sem pagamento é data pendurada em nada. */
const MENSAGEM_PAGO_EM = 'A data do pagamento vale com "pago" ou "sinal". Sem recebimento, não há data.';
export function conferirDataDoPagamento(valor, ctx) {
    const recebeu = valor.situacaoPagamento === 'Pago' || valor.situacaoPagamento === 'Sinal';
    if (valor.pagoEm && !recebeu) {
        ctx.addIssue({ code: 'custom', path: ['pagoEm'], message: MENSAGEM_PAGO_EM });
    }
}
/** Locação avulsa não exige cadastro de aluno — só quem reservou. */
export const zNovaLocacao = z.object({
    tipo: z.literal('LocacaoAvulsa'),
    ...quando,
    ...apresentacao,
    ...cobranca,
    titulo: texto(80).optional(),
    responsavelNome: z.string().trim().min(2, 'Informe quem reservou.').max(80),
    responsavelTelefone: zTelefone.optional(),
    pessoas: z.number().int().min(1, 'Ao menos uma pessoa.').max(40).optional(),
});
export const zNovoEvento = z.object({
    tipo: z.literal('Evento'),
    ...quando,
    ...apresentacao,
    ...cobranca,
    titulo: z.string().trim().min(2, 'Dê um nome ao evento.').max(80),
    responsavelNome: texto(80).optional(),
    responsavelTelefone: zTelefone.optional(),
    pessoas: z.number().int().min(1).max(200).optional(),
});
/** Bloqueio não tem ninguém: é a quadra fora de uso — "Manutenção da rede". */
export const zNovoBloqueio = z.object({
    tipo: z.literal('Bloqueio'),
    ...quando,
    ...apresentacao,
    titulo: z.string().trim().min(2, 'Diga o motivo do bloqueio.').max(80),
});
const MENSAGEM_TIPO_INDISPONIVEL = 'Aqui nascem locação avulsa, evento e bloqueio. Turma fixa é criada na tela de Turmas; ' +
    'aula experimental, a partir do pedido do site.';
/**
 * O corpo de `POST /agenda/marcacoes`.
 *
 * União discriminada por `tipo` porque os campos obrigatórios mudam:
 * locação exige quem reservou, evento exige nome, bloqueio exige o
 * motivo. É validação por tipo, não estrutura diferente — a tabela
 * continua uma só.
 */
export const zNovaMarcacao = z
    .discriminatedUnion('tipo', [zNovaLocacao, zNovoEvento, zNovoBloqueio], {
    errorMap: (issue, ctx) => issue.code === 'invalid_union_discriminator'
        ? { message: MENSAGEM_TIPO_INDISPONIVEL }
        : { message: ctx.defaultError },
})
    .superRefine((m, ctx) => {
    if (m.recorrencia?.ate && m.recorrencia.ate < m.data) {
        ctx.addIssue({
            code: 'custom',
            path: ['recorrencia', 'ate'],
            message: 'O fim da repetição precisa ser depois da primeira data.',
        });
    }
});
/** O schema de cada tipo, para validar o estado final depois de uma alteração. */
export function schemaDoTipo(tipo) {
    return { LocacaoAvulsa: zNovaLocacao, Evento: zNovoEvento, Bloqueio: zNovoBloqueio }[tipo];
}
export function ehTipoOperante(tipo) {
    return TIPOS_OPERANTES.includes(tipo);
}
/**
 * Os tipos que a agenda COBRA — os que têm valor e pagamento editáveis
 * em `#m-evento`. Turma fixa e aula experimental têm o conteúdo, valores
 * inclusive, editado pelo módulo dono delas: Turmas (documento 10) e
 * Site (14). Pela agenda elas só mudam de horário, quadra e duração.
 */
export const TIPOS_COM_COBRANCA = [
    'LocacaoAvulsa',
    'Evento',
];
export function temCobrancaNaAgenda(tipo) {
    return TIPOS_COM_COBRANCA.includes(tipo);
}
/**
 * O corpo de `PATCH /agenda/marcacoes/:id` — mover, redimensionar,
 * corrigir. Tudo opcional; `null` apaga o campo.
 *
 * Em ocorrência de série, `escopo` é obrigatório — e a ausência dele é
 * recusada no servidor em vez de assumida. Adivinhar "só esta" ou
 * "toda a série" é decidir pela pessoa.
 */
export const zAlteracaoDeMarcacao = z
    .object({
    quadraId: zId.optional(),
    data: zData.optional(),
    inicio: zHoraDaGrade.optional(),
    duracaoMinutos: zDuracaoMinutos.optional(),
    titulo: texto(80).nullable().optional(),
    observacao: texto(500).nullable().optional(),
    responsavelNome: texto(80).nullable().optional(),
    responsavelTelefone: zTelefone.nullable().optional(),
    pessoas: z.number().int().min(1).max(200).nullable().optional(),
    valor: zCentavos.nullable().optional(),
    formaPagamento: z.enum(FORMA_PAGAMENTO).nullable().optional(),
    situacaoPagamento: z.enum(SITUACAO_PAGAMENTO_MARCACAO).nullable().optional(),
    /**
     * A data do recebimento. `null` apaga — é o que "desfazer a baixa"
     * manda junto com a situação de volta para "a pagar".
     *
     * Vai SEMPRE acompanhada da situação: a data sozinha não diria se o
     * dinheiro entrou, e o servidor não adivinha.
     */
    pagoEm: zData.nullable().optional(),
    cor: z.enum(CORES_DE_CARD).nullable().optional(),
    escopo: z.enum(ESCOPOS_DE_ALTERACAO).optional(),
})
    .refine((a) => Object.keys(a).some((chave) => chave !== 'escopo'), {
    message: 'Nada para alterar.',
})
    .superRefine(conferirDataDoPagamento);
/**
 * `#m-baixa` — o corpo da baixa de pagamento de uma marcação.
 *
 * É um SUBCONJUNTO de `zAlteracaoDeMarcacao`, não um endpoint novo: dar
 * baixa é corrigir a cobrança de uma marcação, e o caminho de escrita, a
 * trilha, o acerto do razão e a pergunta da série já são os da alteração.
 * Existe como schema próprio para o formulário do modal validar o que ele
 * de fato manda — e para a situação ser OBRIGATÓRIA aqui, enquanto na
 * alteração geral ela é um campo entre vinte.
 */
export const zBaixaDePagamento = z
    .object({
    situacaoPagamento: z.enum(SITUACAO_PAGAMENTO_MARCACAO, {
        errorMap: () => ({ message: 'Escolha a situação do pagamento.' }),
    }),
    formaPagamento: z.enum(FORMA_PAGAMENTO).nullable().optional(),
    valor: zCentavos.nullable().optional(),
    pagoEm: zData.nullable().optional(),
})
    .superRefine(conferirDataDoPagamento);
/**
 * Cancelar é registro, não `DELETE`: a linha fica, com quem, quando e
 * por quê. A taxa de cancelamento de locação é informação de negócio.
 */
export const zCancelamentoDeMarcacao = z.object({
    motivo: z.string().trim().min(3, 'Diga por que a marcação foi cancelada.').max(200),
    escopo: z.enum(ESCOPOS_DE_ALTERACAO).optional(),
});
/**
 * A verificação ENQUANTO se preenche `#m-reserva` — o bloco vermelho
 * "Conflito. Quadra 1 já tem marcação às 19:00" ou o verde de
 * confirmação. É ergonomia; a garantia é a constraint no banco.
 */
export const zConsultaDeDisponibilidade = z.object({
    ...quando,
    recorrencia: zRecorrencia.optional(),
    /** Ao remarcar, a própria marcação não conflita consigo. */
    ignorar: zId.optional(),
    /**
     * 10 · turmas — o tipo que o formulário vai gravar. Com ele a resposta
     * inclui a alocação fixa (P1), que recusa locação avulsa no horário
     * reservado e aceita bloqueio. Opcional: sem tipo, só o conflito.
     */
    tipo: z.enum(TIPO_MARCACAO).optional(),
});
// ---------------------------------------------------------------------
// Horas a cobrar — o elo entre a agenda e o financeiro
// ---------------------------------------------------------------------
/**
 * `GET /agenda/a-cobrar` — as horas jogadas que a agenda registrou e o
 * caixa ainda não recebeu.
 *
 * O período é fechado nos dois lados, em datas da arena. Sem nada, é
 * HOJE: é o que o painel pergunta.
 */
export const zConsultaDeHorasACobrar = z
    .object({
    de: zData.optional(),
    ate: zData.optional(),
})
    .refine((c) => !c.de || !c.ate || c.de <= c.ate, {
    path: ['ate'],
    message: 'O fim do período precisa ser igual ou depois do início.',
});
//# sourceMappingURL=agenda.js.map